import { useMutation } from '@tanstack/react-query';
import { useCartAttributes } from 'hooks/cart/useCartAttributes';
import { useLocalStorage, UserLocale } from 'hooks/userPreferences';
import { API_ONE_CLICK_CHECKOUT } from 'lib/constants/routes';
import { z } from 'zod';

const attributeSchema = z.object({
  key: z.string(),
  value: z.string(),
});

const attributesSchema = z
  .array(attributeSchema)
  .optional()
  .refine((data) => (data ? data.length <= 250 : true), {
    message: 'The input must not contain more than 250 values',
  });

const lineInputSchema = z.object({
  attributes: attributesSchema,
  merchandiseId: z.string(),
  quantity: z.number().int().optional(),
  sellingPlanId: z.string().optional(),
});

export const oneClickCheckoutSchema = z.object({
  lines: z.array(lineInputSchema),
  discountCodes: z.array(z.string()).optional(),
  countryCode: z.string(),
  attributes: attributesSchema,
});

export const oneClickCheckoutHook = z.object({
  lines: z.array(lineInputSchema),
  discountCodes: z.array(z.string()).optional(),
});

export type OneClickCheckoutSchema = z.infer<typeof oneClickCheckoutSchema>;
export type OneClickCheckoutSchemaHook = z.infer<typeof oneClickCheckoutHook>;

const ONE_CLICK_CHECKOUT_KEY = 'multipass';

const getOneClickCheckoutUrl = async ({
  lines,
  discountCodes,
  countryCode,
  attributes,
}: OneClickCheckoutSchema): Promise<{
  checkoutUrl: string;
}> => {
  try {
    const response = await fetch(API_ONE_CLICK_CHECKOUT, {
      method: 'post',
      body: JSON.stringify({ lines, discountCodes, countryCode, attributes }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(
        response.statusText,
        response as ErrorOptions | undefined,
      );
    }

    return await (response.json() as Promise<{ checkoutUrl: string }>);
  } catch (error) {
    console.error(error);
    throw new Error('One-click-checkout failed');
  }
};

export const useOneClickCheckout = ({
  lines,
  discountCodes,
}: OneClickCheckoutSchemaHook) => {
  const {
    value: { countryCode },
  } = useLocalStorage(UserLocale);
  const { getCartAttributes } = useCartAttributes();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: [ONE_CLICK_CHECKOUT_KEY],
    mutationFn: getOneClickCheckoutUrl,
  });

  const goToOneClickCheckout = async () => {
    if (lines) {
      const attributes = await getCartAttributes();

      const { checkoutUrl } = await mutateAsync({
        lines,
        discountCodes,
        countryCode,
        attributes,
      });

      window.location.assign(checkoutUrl);
    }
  };

  return { isPending, goToOneClickCheckout };
};
